import React, { ReactNode } from "react";
import { HbrButton, HbrIcon, HbrSpinner } from "@harbor/elements-react-standalone";
import { pencilSimple, trash, plus, check, x, arrowRight, question } from "@harbor/elements/icons";


export type StbButtonRole = 'add'|'cancel'|'confirm'|'default'|'delete'|'deny'|'help'|'request'|'save'|'update';
export type StbButtonSize = 'xsmall'|'small'|'medium'
export type StbButtonVariant = 'outline'|'text'|'fill';
export type StbButtonSentiment = 'interact'|'success'|'danger'|'info'|'warning';

const buttonRole: Record<StbButtonRole, {
  icon: () => string,
  label: string,
  sentiment: StbButtonSentiment
}> = {
  update: {
    icon: pencilSimple,
    label: 'Update',
    sentiment: 'interact'
  },
  save: {
    icon: check,
    label: 'Save',
    sentiment: 'success'
  },
  cancel: {
    icon: x,
    label: 'Cancel',
    sentiment: 'interact'
  },
  add: {
    icon: plus,
    label: 'Add',
    sentiment: 'success'
  },
  delete: {
    icon: trash,
    label: 'Delete',
    sentiment: 'danger'
  },
  request: {
    icon: arrowRight,
    label: 'Request',
    sentiment: 'interact'
  },
  help: {
    icon: question,
    label: 'Help',
    sentiment: 'interact'
  },
  confirm: {
    icon: check,
    label: 'Confirm',
    sentiment: 'success'
  },
  deny: {
    icon: x,
    label: 'Deny',
    sentiment: 'success'
  },
  default: {
    icon: null,
    label: 'Close',
    sentiment: 'info'
  }
}

const buttonIconOnly: Record<StbButtonRole, () => string> = {
  ['update']: pencilSimple,
  ['save']: check,
  ['cancel']: x,
  ['add']: plus,
  ['delete']: trash,
  ['default']: check,
  ['request']: arrowRight,
  ['help']: question,
  ['confirm']: check,
  ['deny']: x,
};

interface StbButtonProps {
  onClick?: React.MouseEventHandler<HTMLHbrButtonElement>
  children?: ReactNode;
  role?: StbButtonRole;
  size?: StbButtonSize;
  pill?: boolean;
  variant?: StbButtonVariant;
  sentiment?: StbButtonSentiment;
  disabled?: boolean;
  iconOnly?: boolean;
  icon?: () => string;
  loading?: boolean;
  className?: string;
}

const StbButton: React.FC<StbButtonProps> = ({
  onClick,
  children = null,
  role = 'default',
  size = 'small',
  pill = true,
  variant = 'outline',
  sentiment = 'info',
  disabled = false,
  iconOnly = false,
  icon = null,
  loading = false,
  className = '',
}: StbButtonProps) => {
  const _children = iconOnly
    ? ''
    : children ?? buttonRole[role].label ?? '';
  const _isEmptyLabel = !String(_children).trim().length;

  const _iconOnly = (size === 'xsmall' && variant !== 'text')
    ? false                                                     // using label for 'padding' right side of xsmall button
    : (iconOnly || _isEmptyLabel);
  const _icon = icon ?? (_isEmptyLabel
    ? buttonIconOnly[role]
    : buttonRole[role].icon);

  const _sentiment = role === 'default'
    ? sentiment
    : buttonRole[role].sentiment;
  const _variant = variant;
  
  const _className = `${iconOnly ? 'stb-button--icon-only' : ''} ${loading ? 'stb-button--loading' : ''} ${className}`;

  return (
    <HbrButton
      variant={_variant}
      size={size}
      pill={pill}
      onClick={onClick}
      sentiment={_sentiment}
      disabled={disabled || loading}
      className={`stb-button ${_className}`}
    >
      {/* ICON */}
      {loading && _icon && <HbrSpinner
        slot="prefix"
        size="small"
      />}
      {!loading && _icon && <HbrIcon 
        asset={_icon}
        slot="prefix"
      />}

      {/* LABEL */}
      {!_iconOnly &&
        (<div>
          {_variant === 'text' ? (<span>&nbsp;</span>) : null}
          {_children}
          {_variant === 'text' ? (<span>&nbsp;</span>) : null}
        </div>)
      }
    </HbrButton>)
};

StbButton.displayName = "StbButton";
export default StbButton;